export const getAbbreviatedName = (fullName: string) => {
  if (!fullName) return '';
  const parts = fullName.split(' ');
  if (parts.length < 2) return fullName;

  const lastName = parts[0];
  const firstNameInitial = parts[1]?.[0] || '';
  const patronymicInitial = parts[2]?.[0] || '';
  let result = `${lastName}`;
  if (firstNameInitial) result += ` ${firstNameInitial}.`;
  if (patronymicInitial) result += ` ${patronymicInitial}.`;
  return result.trim();
};

export const getRecordWordForm = (words: string[], count: number) => {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
    return `${count} ${words[0]}`;
  }

  if (lastDigit === 1) {
    return `${count} ${words[1]}`;
  }

  if (lastDigit >= 2 && lastDigit <= 4) {
    return `${count} ${words[2]}`;
  }

  return `${count} ${words[3]}`;
};

type TUserNamesKeys = 'firstName' | 'lastName' | 'middleName';
type TUserNamesSnakeCaseKeys = 'first_name' | 'last_name' | 'middle_name';
type TUserNames = Partial<
  Record<TUserNamesKeys | TUserNamesSnakeCaseKeys, string | null>
> | null;

/**
 * Форматирует имена пользователя, преобразуя ключи camelCase или snake_case в camelCase,
 * применяя значения по умолчанию, если они предоставлены или если имена отсутствуют.
 *
 * @param userNames - Объект имен пользователя, содержащий firstName, lastName, middleName
 *                    в формате camelCase или snake_case, или null.
 * @param defaultValues - Необязательные значения по умолчанию для firstName, lastName, middleName.
 * @returns Объект с ключами в формате camelCase (firstName, lastName, middleName) и строковыми значениями.
 */
const formatUserNames = (
  userNames: TUserNames,
  defaultValues?: Partial<Record<TUserNamesKeys, string>>
): Record<TUserNamesKeys, string> => {
  const {
    firstName: firstNameDefault = '',
    lastName: lastNameDefault = '',
    middleName: middleNameDefault = ''
  } = defaultValues || {};
  if (!userNames)
    return {
      firstName: firstNameDefault,
      lastName: lastNameDefault,
      middleName: middleNameDefault
    };
  return {
    firstName: userNames.firstName || userNames.first_name || firstNameDefault,
    lastName: userNames.lastName || userNames.last_name || lastNameDefault,
    middleName:
      userNames.middleName || userNames.middle_name || middleNameDefault
  };
};

/**
 * Формирует полное имя пользователя на основе предоставленных имен, шаблона и конфигурации.
 *
 * @param userNames - Объект имен пользователя, содержащий firstName, lastName, middleName
 *                    в формате camelCase или snake_case, или null.
 * @param config - Объект конфигурации для управления форматированием имени.
 * @param config.hide - Необязательные флаги для скрытия определенных частей имени (firstName, lastName, middleName).
 * @param config.defaultValues - Необязательные значения по умолчанию для частей имени или результата.
 * @param config.pattern - Порядок частей имени (по умолчанию 'last_first_middle').
 * @returns Отформатированная строка полного имени или значение по умолчанию для результата, если имя не сформировано.
 */
export const getUserFullName = (
  userNames: TUserNames,
  config?: {
    hide?: Partial<Record<TUserNamesKeys, boolean>>;
    defaultValues?: Partial<Record<TUserNamesKeys | 'result', string>>;
    pattern?:
      | 'last_first_middle'
      | 'first_middle_last'
      | 'first_last_middle'
      | 'first_last';
  }
): string => {
  const { hide, defaultValues, pattern = 'last_first_middle' } = config || {};
  const formattedUserNames = formatUserNames(userNames, defaultValues);

  let result = '';
  const appendPart = (key: TUserNamesKeys) => {
    const part = formattedUserNames[key];
    if (!hide?.[key] && part) {
      result += (result ? ' ' : '') + part;
    }
  };

  (
    ({
      first_last: () => {
        appendPart('firstName');
        appendPart('lastName');
      },
      first_last_middle: () => {
        appendPart('firstName');
        appendPart('lastName');
        appendPart('middleName');
      },
      first_middle_last: () => {
        appendPart('firstName');
        appendPart('middleName');
        appendPart('lastName');
      },
      last_first_middle: () => {
        appendPart('lastName');
        appendPart('firstName');
        appendPart('middleName');
      }
    }) satisfies Record<typeof pattern, () => void>
  )[pattern]();

  return result || defaultValues?.result || '';
};

export const checkPercentageValue = (value: string) => {
  const cleanedValue = value.replace(/[^0-9.]/g, '');
  const numberValue = Number(cleanedValue);
  if (Number.isNaN(numberValue)) return false;
  if (numberValue < 0) return false;
  if (numberValue > 100) return false;
  const [integerPart, decimalPart] = cleanedValue.split('.');
  if (integerPart && integerPart.length > 2) return false;
  if (decimalPart && decimalPart.length > 3) return false;
  return true;
};
